import { useEffect, RefObject } from 'react';

/**
 * Collapses the banner container when the injected iframe has height ≤ 1px,
 * indicating the ad library moved the banner elsewhere (ghost slot).
 * Sets `data-ghost="true"` on the container; CSS hides it.
 */
export function useCollapseOnGhostBanner(
  enabled: boolean,
  slotRef: RefObject<HTMLElement | null>,
  containerRef: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const slotEl = slotRef.current;
    const containerEl = containerRef.current;
    if (!slotEl || !containerEl) return;

    let iframeObserver: ResizeObserver | null = null;

    const checkGhost = () => {
      const iframe = slotEl.querySelector('iframe') as HTMLIFrameElement | null;
      if (!iframe) return;

      // Start observing the iframe once it's found
      if (!iframeObserver) {
        iframeObserver = new ResizeObserver(() => {
          const h = iframe.getBoundingClientRect().height;
          if (h <= 1) {
            containerEl.setAttribute('data-ghost', 'true');
          } else {
            containerEl.removeAttribute('data-ghost');
          }
        });
        iframeObserver.observe(iframe);
      }

      // Also check immediately
      const h = iframe.getBoundingClientRect().height;
      if (h <= 1) {
        containerEl.setAttribute('data-ghost', 'true');
      } else {
        containerEl.removeAttribute('data-ghost');
      }
    };

    // Watch for iframe insertion
    const mutationObserver = new MutationObserver(checkGhost);
    mutationObserver.observe(slotEl, { childList: true, subtree: true });

    // Fallback: poll for iframe (in case mutation observer misses it)
    const intervalId = window.setInterval(checkGhost, 300);

    // Initial check
    checkGhost();

    return () => {
      mutationObserver.disconnect();
      if (iframeObserver) iframeObserver.disconnect();
      window.clearInterval(intervalId);
    };
  }, [enabled, slotRef, containerRef]);
}

export default useCollapseOnGhostBanner;
